import React, { useMemo } from "react";

import {
  IMessageAppealStrategy,
  IMessageMetadata,
} from "../../../../types/chat";
import { ChatHelper } from "../../../helpers/chatHelper";
import { MarkdownDataTableColumn } from "./ChatMarkdownDataTable";
import ChatMessageExpandableTable from "./ChatMessageExpandableTable";
import ChatMessageUnavailable from "./ChatMessageUnavailable";
import MarkdownRenderer from "../../../ui/MarkdownRenderer";
import CoreExpander from "../../../ui/core/CoreExpander";
import { Typography } from "@mui/material";

const FIELD_VALUE_COLUMNS: MarkdownDataTableColumn[] = [
  { value: "field", label: "Field" },
  { value: "value", label: "Value" },
];

// All displayable IMessageAppealStrategy fields (excludes section_labels/section_display_order,
// which are display metadata, not data). Order here is the fallback/append order used whenever
// the backend's section_display_order (which only lists a curated subset) omits a field, so every
// property is always shown.
const SUPPORTED_SECTIONS: string[] = [
  "header",
  "disposition",
  "denial_classification",
  "exit_reason",
  "appeal_success_likelihood",
  "likelihood_rationale",
  "lines_for_appeal",
  "lines_for_correction",
  "correction_guidance",
  "payer_cited_policy",
  "supporting_policies",
  "contradicting_policies",
  "policies_found",
  "overall_documentation_strength",
  "lines_with_strong_documentation",
  "lines_with_absent_documentation",
  "diagnosis_mismatch_count",
  "evidence_types_absent_by_line",
  "key_clinical_quotes",
  "valid_auth_found",
  "primary_arguments",
  "potential_weaknesses",
  "payer_specific_considerations",
  "proposed_approach",
  "recommended_justification_template",
  "recommended_letter_template",
  "letter_template_rationale",
  "selected_letter_template",
  "next_steps",
  "iteration",
  "analyst_feedback_incorporated",
];

const DEFAULT_EXPANDED_SECTIONS: string[] = ["disposition", "next_steps"];

/** True when the object carries something this card can draw.
 * A fenced JSON reply with a different shape (for example a QC verdict)
 * must not be drawn as this card — every known section would be skipped
 * and the bubble would be empty. A section named only in
 * section_display_order still counts, so a new backend field is shown. */
function hasAppealStrategySection(parsed: IMessageAppealStrategy): boolean {
  const record = parsed as Record<string, unknown>;
  const sections = parsed.section_display_order?.length
    ? parsed.section_display_order
    : SUPPORTED_SECTIONS;
  return sections.some((section) => {
    const value = record[section];
    return value != null && value !== "";
  });
}

interface ChatMessageAppealStrategyProps {
  data: IMessageAppealStrategy | string;
  metadata?: IMessageMetadata;
}

const parseAppealStrategy = (
  data: IMessageAppealStrategy | string,
): IMessageAppealStrategy | null => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data) as IMessageAppealStrategy;
  } catch {
    return null;
  }
};

/** Ex: total_patient_responsibility -> Total Patient Responsibility */
const formatFieldKey = (key: string): string => {
  if (!key) return "";

  return key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const formatScalarValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const objectToFieldValueRows = (object: Record<string, unknown>) =>
  Object.entries(object).map(([key, value]) => ({
    field: formatFieldKey(key),
    value,
  }));

const isBlankCell = (value: unknown): boolean =>
  value == null || value === "" || (Array.isArray(value) && value.length === 0);

// quote_refs are 0-based indexes into key_clinical_quotes, meaningless to a reader.
const HIDDEN_COLUMNS = new Set(["quote_refs"]);

const deriveColumnsFromObjects = (
  items: Record<string, unknown>[],
): MarkdownDataTableColumn[] => {
  const keys: string[] = [];

  items.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    });
  });

  return keys
    .filter((key) => !HIDDEN_COLUMNS.has(key))
    .filter((key) => items.some((item) => !isBlankCell(item[key])))
    .map((key) => ({ value: key, label: formatFieldKey(key) }));
};

const ChatMessageAppealStrategy: React.FC<ChatMessageAppealStrategyProps> = ({
  data,
  metadata,
}) => {
  const parsed = useMemo(() => parseAppealStrategy(data), [data]);
  // REV-2299: section_display_order is a curated subset — fall back to every
  // known section (not []) so a missing/empty order never renders a blank card.
  const order =
    parsed?.section_display_order && parsed?.section_display_order.length > 0
      ? parsed?.section_display_order
      : SUPPORTED_SECTIONS;

  if (!parsed || !hasAppealStrategySection(parsed)) {
    // Don't render an empty bubble when the JSON fails to parse, or when it
    // parsed but has none of the sections this card knows how to draw.
    return <ChatMessageUnavailable raw={data} />;
  }

  return (
    <>
      {parsed?.title ? (
        <Typography
          sx={{ marginBottom: "25px", fontSize: "16px", fontWeight: "bold" }}
        >
          {parsed.title}
        </Typography>
      ) : null}

      {order.map((section, index) => {
        // REV-2299: a section absent from SUPPORTED_SECTIONS (e.g. a new or
        // renamed backend field not yet added here) still renders through the
        // generic field/value paths below instead of silently vanishing.
        const value = (parsed as Record<string, unknown>)[section];
        if (value === null || value === undefined || value === "") {
          return null;
        }

        const title =
          parsed.section_labels?.[section] ?? formatFieldKey(section);
        const defaultExpanded =
          index === 0 || DEFAULT_EXPANDED_SECTIONS.includes(section);

        // List of strings/numbers -> map of markdowns (bullet list)
        // List of objects -> table
        if (Array.isArray(value)) {
          if (value.length === 0) return null;

          if (isPlainObject(value[0])) {
            const rows = value as unknown as Record<string, unknown>[];
            const columns = deriveColumnsFromObjects(rows);

            return (
              <ChatMessageExpandableTable
                key={section}
                title={title}
                defaultExpanded={defaultExpanded}
                columns={columns}
                data={rows}
                metadata={metadata}
              />
            );
          }

          const listMarkdown = (value as (string | number)[])
            .map((item) => `- ${item}`)
            .join("\n");

          return (
            <CoreExpander
              key={section}
              title={title}
              defaultExpanded={defaultExpanded}
            >
              <MarkdownRenderer content={listMarkdown} metadata={metadata} />
            </CoreExpander>
          );
        }

        // Nested object with explicit columns/fields -> table using the given column labels
        if (isPlainObject(value) && ChatHelper.isColumnsFieldsObject(value)) {
          const columns: MarkdownDataTableColumn[] = [
            { value: "label", label: value.columns[0] },
            { value: "value", label: value.columns[1] },
          ];

          return (
            <ChatMessageExpandableTable
              key={section}
              title={title}
              defaultExpanded={defaultExpanded}
              columns={columns}
              data={value.fields}
              rowKeyField="label"
              metadata={metadata}
            />
          );
        }

        // Nested object (e.g. proposed_approach, evidence_types_absent_by_line) -> field/value table
        if (isPlainObject(value)) {
          const rows = objectToFieldValueRows(value);
          if (!rows.length) return null;

          return (
            <ChatMessageExpandableTable
              key={section}
              title={title}
              defaultExpanded={defaultExpanded}
              columns={FIELD_VALUE_COLUMNS}
              data={rows}
              rowKeyField="field"
              metadata={metadata}
            />
          );
        }

        // Scalar string/number/boolean -> markdown
        return (
          <CoreExpander
            key={section}
            title={title}
            defaultExpanded={defaultExpanded}
          >
            <MarkdownRenderer
              content={formatScalarValue(value)}
              metadata={metadata}
            />
          </CoreExpander>
        );
      })}
    </>
  );
};

export default ChatMessageAppealStrategy;
